import { Flex, Icon, Text, useDisclosure } from '@chakra-ui/react';
import { Download01 } from '@kula-ai/icons/outline/Download01';
import { Mail01 } from '@kula-ai/icons/outline/Mail01';
import { Menu } from '@/components/ats';
import { Plus } from '@kula-ai/icons/outline/Plus';
import { Copy03 } from '@kula-ai/icons/dual-tone/Copy03';
// import { LiaHashtagSolid } from 'react-icons/lia';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { Eye } from '@kula-ai/icons/outline/Eye';
import { Users01 } from '@kula-ai/icons/outline/Users01';
import { Lock01 } from '@kula-ai/icons/outline/Lock01';
import { VisibleMenu } from '@/pages/reports/custom-reports/show/headerActions/visibleMenu';
import { useState, useMemo } from 'react';
import { DeleteConfirmationModal } from '@/componentsV1/Modal/DeleteConfirmationModal';
import { API_SERVICE } from '../../api/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { CreateEditCustomReportModal } from '@/pages/reports/custom-reports/components/CreateEditCustomReportModal';
import { useEffect } from 'react';
import { ScheduleReportsEmail } from '@/pages/reports/ats-reports/components/ScheduleReportsEmail';
// import { BookOpen01 } from '@kula-ai/icons/outline/BookOpen01';
import { AvatarList } from '@/componentsV1/Avatar/AvatarList';
import { SelectMembers } from './selectMembers';
import { DownloadReports } from './DownloadReports';
import { useDownloadReports } from '@/hooks';
import { useSelector } from 'react-redux';
import { getCustomReportData } from '@/store/custom-report';
import { getStaticReportData } from '@/store/static-report';
import { useShowToast } from '@/hooks';
import { KText } from '@/ui-library/components/text';
import { Button } from '@kula-ai/components';
import { Edit03, Edit05, PlusCircle } from '@kula-ai/icons-core';
import { usePermission } from '@/ui/permission';
import { useKFlags } from '@/hooks/use-k-flags';
import { reportUtils } from '@/pages/reports/reports-main/utils/reportUtils';

// Helper function to generate layout data
const generateLayoutData = (data = [], currentLayoutConfig) => {
  if (!currentLayoutConfig || currentLayoutConfig.length === 0) {
    return [];
  }

  return currentLayoutConfig.map((layout, index) => {
    const tileData = data[index];
    return {
      ...layout,
      key: tileData?.id?.toString() || layout.key,
    };
  });
};

export const HeaderActions = ({
  customReport = {},
  selectedFilters,
  initiateTileSelection = () => {},
  hasWriteAccess = false,
  isOwner = false,
  isStaticReport = false,
  isEditMode = false,
  setEditMode = undefined,
}) => {
  const queryClient = useQueryClient();
  const toast = useShowToast();
  const history = useHistory();
  const { feReportsV3 } = useKFlags();
  const scheduleEmailActions = useDisclosure();
  const { filterParams, ...otherFilters } = useSelector(
    isStaticReport ? getStaticReportData : getCustomReportData
  );
  const downloadReportData = {
    ...customReport,
    selected: selectedFilters,
    ...otherFilters,
    isStaticReport,
  };

  const { handleDownload, isPolling } = useDownloadReports(downloadReportData);

  const [members, setMembers] = useState([]);
  const setVisibleMembers = (reportId) => {
    const cachedCustomReport = queryClient.getQueryData([
      `getCustomReports/${reportId}`,
    ]);

    const members =
      cachedCustomReport?.data?.members?.map((member) => {
        return { ...member?.user, access: member?.access };
      }) ?? [];

    if (members.length > 0) {
      setMembers(members);
    }
  };

  const deleteReportMutation = useMutation({
    mutationFn: API_SERVICE.deleteCustomReport,
    onSuccess: () => {
      toast({
        title: 'Report deleted',
      });
      history.push('/reports');
    },
  });

  const visibilityMutation = useMutation({
    mutationFn: (data) =>
      API_SERVICE.updateCustomReport(data['reportID'], data['payload']),
    onSuccess: (updatedData) => {
      showVisibilitySuccessToast();
      // ✅ update the report detail
      queryClient.setQueryData([`getCustomReports/${customReport.id}`], {
        data: updatedData,
      });

      // ✅ update all the lists that contain this report
      queryClient.setQueriesData(['getCustomReports'], (previous) =>
        previous.map((report) =>
          report.id === customReport.id ? updatedData : report
        )
      );
    },
  });

  // Mutations for static report cloning
  const addTilesMutation = useMutation({
    mutationFn: (data) =>
      API_SERVICE.addTiles(data['reportID'], data['payload']),
  });

  const layoutMutation = useMutation({
    mutationFn: (data) =>
      API_SERVICE.updateCustomReport(data['reportID'], data['payload']),
  });

  const showVisibilitySuccessToast = () => {
    toast({
      title: 'Visibility settings updated!',
      status: 'success',
      position: 'bottom-left',
      duration: 2000,
      isClosable: true,
    });
  };

  const [showSelectMembersModal, setShowSelectMembersModal] = useState(false);
  const [selectedVisibility, setSelectedVisibility] = useState({});
  const [showDeleteModal, setshowDeleteModal] = useState(false);
  const [showCloneReportModal, setShowCloneReportModal] = useState(false);

  const getSelectedMembers = useMemo(() => {
    if (!members || members.length === 0) return null;

    const viewers = members?.filter((member) => member?.access === 'read');
    const editors = members?.filter((member) => member?.access === 'write');

    return () => (
      <Flex flexDir={'column'}>
        <Flex gap="102px">
          <Text fontWeight={'500'} fontSize="12px">
            <Flex gap="6px" alignItems="center">
              <Icon
                className="primary-900"
                color="primary-900"
                w="16px"
                h="16px"
                as={Users01}
              ></Icon>
              <KText size={'sm-medium'} color="secondary-900" as="span">
                Shared with selected users
              </KText>
            </Flex>
          </Text>
          <Flex alignItems="center" gap="6px">
            <Edit03 className="!text-primary-600" size={16} />
            <KText color="primary-600" hover="none !important">
              Edit
            </KText>
          </Flex>
        </Flex>
        <Flex flexDirection={'column'} maxHeight="181px" overflowY="auto">
          {viewers?.length > 0 && (
            <>
              <KText
                size={'xs-medium'}
                color="secondary-700"
                mt="10px"
                mb="10px"
              >
                VIEWERS
              </KText>
              <AvatarList
                avatarList={viewers}
                showName={true}
                flexDir={'column'}
                alignItems={'baseline'}
                gap={'10px'}
                size={'sm-medium'}
                color="secondary-900"
                props={{
                  className: 'avatar-xs',
                  width: '22px',
                  height: '22px',
                  borderRadius: '8px',
                  border: '2px solid #fff',
                }}
              />
            </>
          )}
          {editors?.length > 0 && (
            <>
              <KText
                size={'xs-medium'}
                color="secondary-700"
                mt="10px"
                mb="10px"
              >
                EDITORS
              </KText>
              <AvatarList
                avatarList={editors}
                showName={true}
                flexDir={'column'}
                alignItems={'baseline'}
                gap={'10px'}
                size={'sm-medium'}
                props={{
                  className: 'avatar-xs',
                  width: '22px',
                  height: '22px',
                  borderRadius: '8px',
                  border: '2px solid #fff',
                }}
              />
            </>
          )}
        </Flex>
      </Flex>
    );
  }, [members]);

  // const showDataBasedActions = customReport?.tiles?.length > 0;

  const canCloneCustomReport =
    usePermission({
      permission: 'api_ats_v1_reports_custom_report.clone?',
    }) &&
    feReportsV3 &&
    customReport?.tiles?.[0]?.subject_type !== 'recruiter_performance';

  // disabled Download and Schedule email since we are having issue with downloaded PDF report
  // we will enable it once we are ready with the fix
  const menuItems = [
    canCloneCustomReport && {
      id: 'clone',
      icon: Copy03,
      label: 'Clone report',
      iconProps: {
        color: '#000',
      },
      onClick: () => {
        setShowCloneReportModal(true);
      },
    },
    {
      id: 'download',
      icon: Download01,
      label: 'Download as PDF',
      iconProps: {
        color: '#000',
      },
      onClick: handleDownload,
    },
    // There is a BE bug, once fixed we can enable this

    {
      id: 'schedule',
      icon: Mail01,
      label: 'Schedule email',
      onClick: () => {
        scheduleEmailActions?.onOpen();
      },
      iconProps: {
        color: '#000',
      },
    },
    // {
    //   id: 'slack',
    //   icon: LiaHashtagSolid,
    //   label: 'Share via Slack',
    //   iconProps: {
    //     color: '#000',
    //   },
    // },
    isOwner && {
      id: 'delete',
      icon: Trash01,
      label: 'Delete report',
      iconProps: {
        color: '#000',
      },
    },
  ].filter(Boolean);

  const visibleFilters = useMemo(() => {
    const hasMembers = members?.length > 0;

    return [
      {
        id: 'selected_members',
        description: hasMembers
          ? null
          : 'View-only or edit access to selected users',
        icon: Users01,
        label: 'Selected users',
        iconProps: { color: '#000' },
        serverRef: 'member',
        Comp: getSelectedMembers,
      },
      {
        id: 'everyone',
        icon: Eye,
        label: 'Everyone',
        description: 'View-only access to all users in Kula',
        iconProps: { color: '#000' },
        serverRef: 'public',
      },
      {
        id: 'me',
        icon: Lock01,
        label: 'Only me',
        description: 'Visible only to you',
        iconProps: { color: '#000' },
        serverRef: 'private',
      },
    ];
  }, [members?.length, getSelectedMembers]);

  const initiateCustomReport = () => {
    initiateTileSelection();
  };

  const handleActionClick = (selectedItem) => {
    const { id } = selectedItem;
    switch (id) {
      case 'delete':
        setshowDeleteModal(true);
        break;
      case 'clone':
        setShowCloneReportModal(true);
        break;
      default:
        break;
    }
  };

  const handleVisibilityActionClick = (selectedItem) => {
    let { serverRef } = selectedItem;
    if (serverRef === 'member') {
      setShowSelectMembersModal(true);
    } else {
      setMembers([]);
      // for my_only and eveyone access
      setSelectedVisibility(selectedItem);
      visibilityMutation.mutate({
        reportID: customReport.id,
        payload: { visibility: serverRef },
      });
    }
  };

  const initiateDeleteReport = () => {
    deleteReportMutation.mutate(customReport.id);
  };

  const setDefaultVisibility = () => {
    const { visibility, members } = customReport ?? {};

    if (visibility === 'member') {
      if (members && members.length > 0) {
        const transformedMembers = members.map((member) => ({
          ...member?.user,
          access: member?.access,
        }));
        setMembers(transformedMembers);
      } else {
        setVisibleMembers(customReport.id);
      }
    }
  };

  useEffect(() => {
    setDefaultVisibility();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (customReport?.members && customReport?.visibility === 'member') {
      const transformedMembers = customReport.members.map((member) => ({
        ...member?.user,
        access: member?.access,
      }));
      setMembers(transformedMembers);
    } else if (customReport?.visibility !== 'member') {
      setMembers([]);
    }
  }, [customReport?.visibility, customReport?.members]);

  // Update selectedVisibility when customReport or visibleFilters change
  useEffect(() => {
    if (customReport?.visibility) {
      const matchingFilter = visibleFilters.find(
        (filter) => filter.serverRef === customReport.visibility
      );
      if (matchingFilter && matchingFilter.id !== selectedVisibility?.id) {
        setSelectedVisibility(matchingFilter);
      }
    }
  }, [customReport?.visibility, visibleFilters, selectedVisibility?.id]);

  return (
    <Flex gap="6px" h="32px" flexShrink={0} minW="fit-content">
      {isOwner && (feReportsV3 ? !isEditMode : true) && (
        <VisibleMenu
          border="none"
          height={'34px'}
          menuItems={visibleFilters}
          menuButtonLabel="Shared with"
          menuButtonProps={{
            //@ts-ignore
            border: 'transparent',
            boxShadow: 'transparent',
            hover: 'none',
          }}
          placement="bottom-end"
          selectedItem={selectedVisibility}
          handleActionClick={handleVisibilityActionClick}
        />
      )}
      {hasWriteAccess && (feReportsV3 ? isEditMode : true) && (
        <Button
          height={'34px'}
          onClick={() => {
            initiateCustomReport();
          }}
          variant={feReportsV3 ? 'tertiary' : 'primary'}
          leftElement={
            feReportsV3 ? (
              <PlusCircle size={16} color="!text-primary-500" />
            ) : (
              <Icon w="18px" h="18px" as={Plus} />
            )
          }
        >
          Add block
        </Button>
      )}
      {!isStaticReport && !feReportsV3 && (
        <Button
          height={'34px'}
          onClick={() => setShowCloneReportModal(true)}
          variant="quaternary"
          leftElement={<Icon w="16px" h="16px" as={Copy03} />}
        >
          Clone report
        </Button>
      )}
      {feReportsV3 && hasWriteAccess && !isEditMode && (
        <Button
          height={'34px'}
          onClick={() => setEditMode?.(true)}
          variant="tertiary"
          leftElement={<Edit05 size={16} />}
        >
          Edit
        </Button>
      )}
      {menuItems?.length > 0 && (feReportsV3 ? !isEditMode : true) && (
        <Menu
          menuItems={menuItems}
          menuButtonLabel="More"
          menuButtonProps={{
            //@ts-ignore
            variant: 'quaternary',
            boxShadow: 'none',
            height: '32px',
          }}
          placement="bottom-end"
          menulistProps={{
            zIndex: '9',
          }}
          handleActionClick={handleActionClick}
        />
      )}
      {showDeleteModal && (
        <DeleteConfirmationModal
          title="Are you sure you want to this report?"
          subTitle="Be aware that once deleted, it cannot be recovered"
          onClose={() => {
            setshowDeleteModal(false);
          }}
          onSubmit={initiateDeleteReport}
          submitButtonLabel="Yes, Delete"
          submitButtonIsLoading={deleteReportMutation.isPending}
          primaryVariant="btn-delete"
        />
      )}
      {showCloneReportModal && (
        <CreateEditCustomReportModal
          type={isStaticReport ? 'cloneStaticReport' : 'clone'}
          report={customReport}
          onClose={() => {
            setShowCloneReportModal(false);
          }}
          onSuccess={
            isStaticReport
              ? async ({ data: createdReportData, setIsCtaLoading }) => {
                  try {
                    // Keep loading state active throughout the entire cloning process
                    setIsCtaLoading(true);
                    const { id: newlyCreatedReportId } =
                      createdReportData || {};

                    // Get tiles data directly from customReport prop (static reports already have the data)
                    if (
                      !customReport.tiles ||
                      customReport.tiles.length === 0
                    ) {
                      toast({
                        title: 'Empty tiles',
                        status: 'error',
                      });
                      setIsCtaLoading(false);
                      return;
                    }

                    // Transform tiles to match the expected API format
                    const tilesData = customReport.tiles.map((tile) => ({
                      subject_type: tile.subject_type,
                      metadata: tile.metadata,
                      query: tile.query,
                    }));

                    // Add tiles to the new custom report
                    addTilesMutation.mutate(
                      {
                        reportID: newlyCreatedReportId,
                        payload: { tiles: tilesData },
                      },
                      {
                        onSuccess: (dataWithTiles) => {
                          // Update the layout of the report using layout from customReport prop
                          const currentLayoutConfig =
                            customReport.layout?.data || [];

                          const layoutData = generateLayoutData(
                            dataWithTiles,
                            currentLayoutConfig
                          );

                          layoutMutation.mutate(
                            {
                              reportID: newlyCreatedReportId,
                              payload: { layout: { data: layoutData } },
                            },
                            {
                              onSuccess: () => {
                                // Show success message before redirect
                                toast({
                                  title: 'Report cloned successfully',
                                  status: 'success',
                                });

                                // Close modal and redirect
                                setIsCtaLoading(false);
                                setShowCloneReportModal(false);
                                const redirectUrl =
                                  reportUtils.getCustomReportUrl(
                                    newlyCreatedReportId,
                                    createdReportData?.name,
                                    feReportsV3
                                  );
                                history.push(redirectUrl);
                              },
                              onError: (error) => {
                                setIsCtaLoading(false);
                                console.error('Layout update error:', error);
                                toast({
                                  title: 'Failed to update report layout',
                                  status: 'error',
                                });
                              },
                            }
                          );
                        },
                        onError: (error) => {
                          setIsCtaLoading(false);
                          console.error('Add tiles error:', error);
                          toast({
                            title: 'Failed to add tiles to report',
                            status: 'error',
                          });
                        },
                      }
                    );
                  } catch (error) {
                    setIsCtaLoading(false);
                    console.error('Static report cloning error:', error);
                    toast({
                      title: 'Failed to clone report',
                      status: 'error',
                    });
                  }
                }
              : undefined
          }
          submitButtonLabel="Clone report"
          modalTitle={{
            headerText: 'Name of cloned report',
            subHeaderText: `Give your report a unique name so it's easier to identify`,
          }}
					skipCTADisabled={true}
        />
      )}
      {showSelectMembersModal && (
        <SelectMembers
          onClose={() => setShowSelectMembersModal(false)}
          onSubmit={() => {
            setShowSelectMembersModal(false);
            showVisibilitySuccessToast();
          }}
          customReport={customReport}
        />
      )}
      <DownloadReports isPolling={isPolling} />
      <ScheduleReportsEmail
        modalActions={scheduleEmailActions}
        currentData={{
          ...customReport,
          selected: selectedFilters,
          ...otherFilters,
        }}
        isCustomReport={!isStaticReport}
      />
    </Flex>
  );
};

export default HeaderActions;
